import { openDB, type IDBPDatabase } from 'idb';
import type { IAnswer, IQuestion, ITestData, RefereeDB } from './types';
import { Question } from './Question';
import { XpManager } from './XpManager';
import { loadQuestions, loadCatalogMetadata, loadCatalogIndex } from '../utils/loadTestData';

// Dynamic catalog languages loaded from metadata
let AVAILABLE_LANGUAGES: Record<string, string[]> = {};
let catalogIndexLoaded = false;

export async function loadAvailableLanguages(): Promise<Record<string, string[]>> {
  if (catalogIndexLoaded) {
    return AVAILABLE_LANGUAGES;
  }

  try {
    const catalogs = await loadCatalogIndex();
    AVAILABLE_LANGUAGES = catalogs.reduce<Record<string, string[]>>((acc, cat) => {
      acc[cat.id] = cat.languages;
      return acc;
    }, {});
    catalogIndexLoaded = true;
  } catch {
    // Fallback if index not yet available
    AVAILABLE_LANGUAGES = {};
  }

  return AVAILABLE_LANGUAGES;
}

export { AVAILABLE_LANGUAGES };

// Leitner-box review delay, in days, before a question in that box becomes
// "due" again. Box 1 has no entry — it is always due immediately.
const BOX_DELAY_DAYS: Record<number, number> = {
  2: 1,
  3: 3,
  4: 7,
  5: 30,
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export class TestDataManager {
  private db: IDBPDatabase<RefereeDB> | null = null;
  private _data: Record<string, Question> = {};
  private loadedLanguages = new Map<string, string[]>();
  private todo: string[] = [];
  private initialized = false;
  private version = '';
  private currentId = '';
  private _asked = 0;
  private _correct = 0;
  private _wrong = 0;
  private _languageMissing = false;
  private _xpManager = new XpManager();

  get data() { return this._data; }
  get asked() { return this._asked; }
  get correct() { return this._correct; }
  get languageMissing() { return this._languageMissing; }
  get availableLanguages() {
    return AVAILABLE_LANGUAGES[this.version] || [];
  }

  get xpManager() { return this._xpManager; }

  get mastered(): number {
    return Object.values(this._data).filter((q) => q.isMastered).length;
  }

  /**
   * Count of questions that are genuinely due right now (box 1, or a
   * higher box whose review delay has actually elapsed). `todo` can also
   * hold a batch of not-yet-due questions pulled forward by `refillTodo()`
   * once the strictly-due queue empties — those must NOT be counted here,
   * or the UI's "due for review" stat would misreport early-review
   * questions as due.
   */
  get dueCount(): number {
    const now = Date.now();
    return this.todo.filter((id) => {
      const question = this._data[id];
      return !question || TestDataManager.nextDueAt(question) <= now;
    }).length;
  }

  get boxDistribution(): number[] {
    const dist = [0, 0, 0, 0, 0];
    for (const q of Object.values(this._data)) {
      dist[q.box - 1]++;
    }
    return dist;
  }

  get globalStreak(): number {
    const answered = Object.values(this._data)
      .filter((q): q is Question & { lastAnswered: Date } => q.lastAnswered !== null)
      .sort((a, b) => +b.lastAnswered - +a.lastAnswered);

    let streak = 0;
    for (const q of answered) {
      if (q.currentStreak === 0) {
        break;
      }
      streak++;
    }
    return streak;
  }

  private getDb = async (): Promise<IDBPDatabase<RefereeDB> | null> => {
    if (this.db) {
      return this.db;
    }
    if (!this.version) {
      return null;
    }
    try {
      const db = await openDB<RefereeDB>(this.version, 1, {
        upgrade(currentDB, oldVersion) {
          if (oldVersion < 1) {
            currentDB.createObjectStore('questions');
          }
        },
      });
      // Browser may close the connection on its own (memory pressure,
      // versionchange from another tab, Safari backgrounding). Drop the
      // cached handle so the next write re-opens.
      db.addEventListener('close', () => {
        if (this.db === db) {
          this.db = null;
        }
      });
      this.db = db;
      return db;
    } catch {
      return null;
    }
  };

  async initialize(lang: string, version: string): Promise<Question> {
    let language = lang;

    // Load languages dynamically from metadata if not cached
    let availableLangs = AVAILABLE_LANGUAGES[version];
    if (!availableLangs) {
      try {
        const metadata = await loadCatalogMetadata(version);
        AVAILABLE_LANGUAGES[version] = metadata.languages;
        availableLangs = metadata.languages;
      } catch {
        availableLangs = ['en'];
      }
    }

    if (!availableLangs.includes(language)) {
      this._languageMissing = true;
      language = 'en';
    } else {
      this._languageMissing = false;
    }

    if (this.initialized && this.version === version) {
      await this.switchLanguage(language);
      return this._data[this.currentId];
    }

    // Reset counters for new version
    this._asked = 0;
    this._correct = 0;
    this._wrong = 0;
    this._data = {};

    // Close the previous version's connection before opening a new one.
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.version = version;

    const data = await loadQuestions(language, version);
    this.loadedLanguages.set(version, [language]);

    const mappedAnswers = data.answers.reduce<Record<string, IAnswer>>((acc, a) => {
      acc[a.id] = a;
      return acc;
    }, {});

    const mappedQuestions = data.questions.reduce<Record<string, IQuestion>>((acc, q) => {
      acc[q.id] = q;
      return acc;
    }, {});

    const mappedTestData: Record<string, ITestData> = {};
    const ids = Object.keys(mappedAnswers);
    this.todo = [...ids];

    try {
      const db = await this.getDb();
      if (!db) {
        throw new Error('IndexedDB unavailable');
      }

      const tx = db.transaction('questions', 'readonly');
      let cursor = await tx.store.openCursor();
      const now = new Date();

      while (cursor) {
        mappedTestData[cursor.key] = cursor.value;
        const td = cursor.value;
        this._asked += td.asked;
        this._correct += td.correct;
        this._wrong += td.wrong;

        if (td.lastAsked) {
          const diffDays = Math.floor(
            Math.abs(+td.lastAsked - +now) / (1000 * 60 * 60 * 24),
          );
          const delayDays = BOX_DELAY_DAYS[td.box];
          const shouldSkip = delayDays !== undefined && diffDays < delayDays;

          if (shouldSkip) {
            const idx = this.todo.indexOf(cursor.key);
            if (idx > -1) {
              this.todo.splice(idx, 1);
            }
          }
        }

        cursor = await cursor.continue();
      }

      await tx.done;
    } catch {
      this.db = null;
    }

    for (const id of ids) {
      this._data[id] = new Question(
        language,
        mappedQuestions[id],
        mappedAnswers[id],
        mappedTestData[id],
      );
    }

    this.initialized = true;

    await this._xpManager.initialize();

    return this.next();
  }

  async checkAnswer(answers: string[]) {
    if (!this.currentId) {
      return { correct: [], rules: [], answeredCorrect: false };
    }

    const question = this._data[this.currentId];
    const previousBox = question.box;
    const result = question.checkAnswer(answers);

    this._asked += 1;
    if (result.answeredCorrect) {
      this._correct += 1;
    } else {
      this._wrong += 1;
    }

    if (this.db) {
      await question.persist(this.db, this.getDb);
    }

    this.syncTodoMembership(question);

    const allRuleQuestions = Object.values(this._data).map((q) => ({
      rule: q.rule,
      isMastered: q.isMastered,
    }));
    const xpResult = this._xpManager.processAnswer(
      result.answeredCorrect,
      previousBox,
      question.box,
      question.rule,
      this.version,
      allRuleQuestions,
    );
    if (xpResult.totalXpGained > 0) {
      await this._xpManager.awardXp(xpResult);
    }
    result.xp = xpResult;

    return result;
  }

  async recordAnswer(questionId: string, answers: string[]) {
    const question = this._data[questionId];
    if (!question) {
      return { correct: [] as string[], rules: [] as string[], answeredCorrect: false };
    }

    const previousBox = question.box;
    const result = question.checkAnswer(answers);

    this._asked += 1;
    if (result.answeredCorrect) {
      this._correct += 1;
    } else {
      this._wrong += 1;
    }

    if (this.db) {
      await question.persist(this.db, this.getDb);
    }

    this.syncTodoMembership(question);

    const allRuleQuestions = Object.values(this._data).map((q) => ({
      rule: q.rule,
      isMastered: q.isMastered,
    }));
    const xpResult = this._xpManager.processAnswer(
      result.answeredCorrect,
      previousBox,
      question.box,
      question.rule,
      this.version,
      allRuleQuestions,
    );
    if (xpResult.totalXpGained > 0) {
      await this._xpManager.awardXp(xpResult);
    }
    result.xp = xpResult;

    return result;
  }

  /**
   * Keeps `todo` in sync with a question's box after an answer: promoted
   * questions (box > 1) leave it, demoted questions (box back to 1) rejoin
   * it if they aren't already present — including questions that are part
   * of a refilled fallback batch (see `refillTodo()`) and are now being
   * re-served even though they weren't strictly due yet.
   */
  private syncTodoMembership(question: Question) {
    const idx = this.todo.indexOf(question.id);
    if (question.box > 1) {
      if (idx > -1) {
        this.todo.splice(idx, 1);
      }
    } else if (idx === -1) {
      this.todo.push(question.id);
    }
  }

  /**
   * Returns how many days must pass since a question's last answer before
   * it becomes due again in its current box. Box 1 is always due.
   */
  private static delayDaysForBox(box: number): number {
    return BOX_DELAY_DAYS[box] ?? 0;
  }

  /**
   * The timestamp (ms since epoch) at which a question becomes due. Used by
   * `dueCount` to tell genuinely-due questions apart from a refilled batch
   * of not-yet-due questions sitting in `todo`. Never-answered or box-1
   * questions are always due, so they sort first (`-Infinity`).
   */
  private static nextDueAt(question: Question): number {
    if (!question.lastAnswered || question.box <= 1) {
      return -Infinity;
    }
    return +question.lastAnswered + TestDataManager.delayDaysForBox(question.box) * MS_PER_DAY;
  }

  /**
   * Refills `todo` when the strictly-due queue is exhausted: finds the
   * lowest-numbered non-empty box among boxes 2-5 (box number is itself a
   * delay tier in this Leitner model, so the lowest non-empty box is the
   * group of questions that is, as a whole, soonest-due) and pushes ALL
   * question ids currently in that box into `todo` as a single batch. This
   * gives a session some variety before the queue needs refilling again —
   * as those questions get answered and promoted further, they leave
   * `todo` (via `syncTodoMembership`) the same way strictly-due questions
   * always have, and once `todo` empties again `next()` calls this again,
   * naturally picking up whichever box is now the lowest non-empty one.
   *
   * Returns `true` when a batch was added, `false` only when there is no
   * question data at all (e.g. catalog failed to load) — the genuine
   * data-load-failure case.
   */
  private refillTodo(): boolean {
    for (let box = 2; box <= 5; box++) {
      const boxMembers = Object.values(this._data).filter((question) => question.box === box);
      if (boxMembers.length > 0) {
        for (const question of boxMembers) {
          if (!this.todo.includes(question.id)) {
            this.todo.push(question.id);
          }
        }
        return true;
      }
    }
    return false;
  }

  /**
   * Jumps directly to a known question id, used only by the D-12 screenshot
   * scenario to reproduce a fixed question deterministically. Returns
   * `undefined` and leaves `currentId` untouched for an unknown id.
   */
  showQuestion(id: string): Question | undefined {
    const question = this._data[id];
    if (!question) {
      return undefined;
    }
    this.currentId = id;
    return question;
  }

  next(): Question {
    if (this.todo.length === 0) {
      this.refillTodo();
    }

    if (this.todo.length > 0) {
      this.currentId = this.todo[Math.floor(Math.random() * this.todo.length)];
      return this._data[this.currentId];
    }

    this.currentId = '';
    return undefined as unknown as Question;
  }

  private async switchLanguage(language: string) {
    const languages = this.loadedLanguages.get(this.version);
    if (languages?.includes(language)) {
      return;
    }

    const data = await loadQuestions(language, this.version);
    for (const question of data.questions) {
      this._data[question.id]?.updateData(language, question);
    }

    this.loadedLanguages.set(this.version, [...(languages ?? []), language]);
  }

  async reset() {
    this._asked = 0;
    this._correct = 0;
    this._wrong = 0;
    await Promise.all(
      Object.values(this._data).map((q) => q.reset(this.db, this.getDb)),
    );
  }

  async resetXp() {
    await this._xpManager.reset();
  }
}
